// Search box autosuggestions: wires autoComplete.js to every `.js-search-suggest`
// input. The form keeps working without JavaScript. See docs/search_autosuggest.md.
import autoComplete from "@tarekraafat/autocomplete.js"

const SELECTOR = ".js-search-suggest"
const DEFAULT_ENDPOINT = "/search/suggestions"
const MIN_CHARS = 3
const DEBOUNCE_MS = 200
const MAX_RESULTS = 8
const LIBRARY_INPUT_ATTRS = [
  "role",
  "aria-owns",
  "aria-haspopup",
  "aria-expanded",
  "aria-controls",
  "aria-autocomplete",
  "aria-activedescendant",
]
const HTML_ESCAPES = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
}

let instances = []
let counter = 0

function escapeHtml(text) {
  return String(text).replace(/[&<>"']/g, (char) => HTML_ESCAPES[char])
}

function escapeRegExp(text) {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

function normalize(value) {
  return value.trim().replace(/\s+/g, " ")
}

// autoComplete.js renders this string with innerHTML, so escape first, then mark
// the query. Must return a non-empty string or the library drops the row.
function highlight(query, record) {
  const value = String(record)
  const found = query ? new RegExp(escapeRegExp(query), "i").exec(value) : null
  if (!found) return escapeHtml(value)

  const start = found.index
  const end = start + found[0].length
  const before = escapeHtml(value.slice(0, start))
  const match = escapeHtml(value.slice(start, end))
  const after = escapeHtml(value.slice(end))
  return `${before}<mark>${match}</mark>${after}`
}

// requestSubmit() dispatches "submit", so views/header/search.js still runs.
function submitForm(form) {
  if (typeof form.requestSubmit === "function") {
    form.requestSubmit()
    return
  }
  const button = form.querySelector(
    'button[type="submit"], input[type="submit"]'
  )
  if (button) {
    button.click()
  } else {
    form.submit()
  }
}

function createInstance(input, form) {
  counter += 1
  const id = counter
  const endpoint = input.dataset.suggestUrl || DEFAULT_ENDPOINT
  let controller = null
  let sequence = 0
  let inflight = false
  let lastResultsQuery = null

  const status = document.createElement("div")
  status.className = "visually-hidden"
  status.setAttribute("aria-live", "polite")
  status.setAttribute("aria-atomic", "true")

  function abort() {
    if (controller) {
      controller.abort()
      controller = null
    }
  }

  function announce(count) {
    if (count > 0) {
      const noun = count === 1 ? "suggestion" : "suggestions"
      status.textContent = `${count} ${noun} available, use up and down arrow keys to navigate`
    } else {
      status.textContent = ""
    }
  }

  // Returning an Error makes autoComplete.js skip rendering (superseded or stale
  // request); returning [] closes the list. It must never throw.
  async function fetchSuggestions(query) {
    abort()
    controller = new AbortController()
    sequence += 1
    const requestId = sequence
    inflight = true

    try {
      const params = new URLSearchParams({ q: query })
      const response = await fetch(`${endpoint}?${params}`, {
        signal: controller.signal,
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
      if (requestId !== sequence) return new Error("stale suggestions response")
      if (!response.ok) return []

      const payload = await response.json()
      if (requestId !== sequence) return new Error("stale suggestions response")

      if (!Array.isArray(payload)) return []
      return payload.filter(
        (item) => typeof item === "string" && item.trim() !== ""
      )
    } catch (error) {
      if (requestId !== sequence || (error && error.name === "AbortError"))
        return new Error("superseded suggestions request")
      return []
    } finally {
      if (requestId === sequence) inflight = false
    }
  }

  // Replaces the library's keydown handler, whose Enter always blocks form
  // submission and whose Escape clears the input.
  function handleKeydown(event) {
    if (event.isComposing || event.keyCode === 229) return

    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp":
        if (ac.isOpen) {
          event.preventDefault()
          if (event.key === "ArrowDown") {
            ac.next()
          } else {
            ac.previous()
          }
        } else if (event.key === "ArrowDown") {
          event.preventDefault()
          if (
            lastResultsQuery !== null &&
            lastResultsQuery === normalize(input.value) &&
            ac.list.childElementCount > 0
          ) {
            ac.open()
            ac.goTo(0)
          } else if (!inflight) {
            ac.start()
          }
        }
        break
      case "Enter":
        if (ac.isOpen && ac.cursor >= 0) {
          event.preventDefault()
          ac.select()
        }
        break
      case "Escape":
        if (ac.isOpen) {
          event.preventDefault()
          ac.close()
        }
        break
      case "Tab":
        if (ac.isOpen) ac.close()
        break
      default:
        break
    }
  }

  const ac = new autoComplete({
    selector: () => input,
    wrapper: false,
    threshold: MIN_CHARS,
    debounce: DEBOUNCE_MS,
    query: normalize,
    data: { src: fetchSuggestions },
    searchEngine: highlight,
    resultsList: {
      id: `search-suggest-list-${id}`,
      class: "dropdown-menu search-suggest-list cy-search-suggest-list",
      maxResults: MAX_RESULTS,
      destination: () => form, // outside the overflow-hidden .input-group
      position: "beforeend",
      element: () => input.removeAttribute("aria-activedescendant"),
    },
    resultItem: {
      id: `search-suggest-option-${id}`,
      class: "dropdown-item search-suggest-item cy-search-suggest-item",
      selected: "active",
    },
    events: {
      input: {
        keydown: handleKeydown,
        selection: (event) => {
          input.value = event.detail.selection.value
          ac.close()
          submitForm(form)
        },
        results: (event) => {
          lastResultsQuery = event.detail.query
          announce(event.detail.results.length)
        },
        close: () => announce(0),
      },
    },
  })

  // ARIA 1.2 combobox attributes
  input.removeAttribute("aria-owns")
  input.setAttribute("aria-haspopup", "listbox")
  input.setAttribute("aria-autocomplete", "list")
  ac.list.setAttribute("aria-label", "Search suggestions")
  ac.list.insertAdjacentElement("afterend", status)

  return { ac, input, status, abort }
}

function destroy({ ac, input, status, abort }) {
  abort()
  ac.close()
  ac.unInit()
  ac.list.remove()
  status.remove()
  LIBRARY_INPUT_ATTRS.forEach((name) => input.removeAttribute(name))
}

function initAll() {
  instances = instances.filter((entry) => {
    if (entry.input.isConnected) return true
    destroy(entry)
    return false
  })

  document.querySelectorAll(SELECTOR).forEach((input) => {
    if (!input.form) return
    if (instances.some((entry) => entry.input === input)) return
    instances.push(createInstance(input, input.form))
  })
}

// Turbolinks snapshots the DOM, so remove everything injected before it caches.
function teardownAll() {
  instances.forEach(destroy)
  instances = []
}

document.addEventListener("turbolinks:load", initAll)
document.addEventListener("turbolinks:before-cache", teardownAll)
